import { useMemo, useState } from 'react';
import { useExecutiveMetrics } from '@/hooks/useExecutiveMetrics';
import { KpiCard } from '@/components/admin/executive/KpiCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useExecutiveUsageMetrics } from '@/hooks/useExecutiveUsageMetrics';
import { useExecutiveSelfPacedMetrics } from '@/hooks/useExecutiveSelfPacedMetrics';
import { AssistantsTab } from '@/components/admin/executive/AssistantsTab';
import { ChallengesTab } from '@/components/admin/executive/ChallengesTab';
import { ContentTab } from '@/components/admin/executive/ContentTab';
import { EngagementTab } from '@/components/admin/executive/EngagementTab';
import { PlatformUsageTab } from '@/components/admin/executive/PlatformUsageTab';
import { SelfPacedTab } from '@/components/admin/executive/SelfPacedTab';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import {
  Users, Rocket, Trophy, DollarSign, Building2, Activity,
  GraduationCap, Sparkles, TrendingUp,
} from 'lucide-react';
import {
  Granularity, RangeKey, resolveRange, defaultGranularity, bucketRows,
  countInRange, sumInRange, pctDelta,
} from '@/lib/timeSeries';

const SOLUTION_LABELS: Record<number, string> = {
  1: 'Contenido con IA',
  2: 'Vibecoding',
  3: 'Asistentes/Agentes',
  4: 'Automatizaciones',
  5: 'Análisis de Datos',
};

const CHART_COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--info))', 'hsl(var(--success))'];

export default function ExecutiveDashboardPage() {
  const { data, isLoading } = useExecutiveMetrics();
  const [range, setRange] = useState<RangeKey>('90d');
  const [granularity, setGranularity] = useState<Granularity>(defaultGranularity('90d'));
  const [tab, setTab] = useState('resumen');

  const dateRange = useMemo(() => resolveRange(range), [range]);

  const { data: usage, isLoading: usageLoading } = useExecutiveUsageMetrics(
    dateRange.from, dateRange.to, granularity, null, tab !== 'resumen' && tab !== 'autoaprendizaje',
  );
  const { data: selfPaced, isLoading: selfPacedLoading } = useExecutiveSelfPacedMetrics(
    dateRange.from, dateRange.to, granularity, null, tab === 'autoaprendizaje',
  );


  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-96" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-72" />
        <Skeleton className="h-72" />
      </div>
    );
  }

  // ---------- Métricas núcleo ----------
  const totalUsers = data.profiles.length;
  const newUsers = countInRange(data.profiles, 'created_at', dateRange.from, dateRange.to);
  const newUsersPrev = countInRange(data.profiles, 'created_at', dateRange.prevFrom, dateRange.prevTo);

  const newProjects = countInRange(data.projects, 'created_at', dateRange.from, dateRange.to);
  const newProjectsPrev = countInRange(data.projects, 'created_at', dateRange.prevFrom, dateRange.prevTo);

  const submittedProjects = data.projects.filter(p => p.submitted).length;
  const evaluatedIds = new Set(data.evaluations.map(e => e.project_id));
  const evaluatedProjects = data.projects.filter(p => evaluatedIds.has(p.id)).length;

  // DAU / WAU / MAU (basado en participant_streaks scope global)
  const globalStreaks = data.streaks.filter(s => !s.scope_type || s.scope_type === 'global');
  const now = new Date();
  const dayAgo = new Date(now); dayAgo.setDate(now.getDate() - 1);
  const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7);
  const monthAgo = new Date(now); monthAgo.setDate(now.getDate() - 30);
  const activeSince = (from: Date) => new Set(
    globalStreaks.filter(s => s.last_activity_date && new Date(s.last_activity_date) >= from).map(s => s.user_id)
  ).size;
  const dau = activeSince(dayAgo);
  const wau = activeSince(weekAgo);
  const mau = activeSince(monthAgo);

  const activeOrgs = data.organizations.filter(o => o.status === 'active').length;
  const activeCohorts = data.cohorts.filter(c => {
    if (!c.end_date) return c.status === 'active' || c.status === 'in_progress';
    return new Date(c.end_date) >= now;
  }).length;

  const activeSubs = data.subscriptions.filter(s => s.status === 'active' || s.status === 'trialing').length;

  const paidPayments = data.payments.filter(p => p.status === 'completed' || p.status === 'succeeded' || p.status === 'paid');
  const revenueInRange = sumInRange(paidPayments, 'created_at', dateRange.from, dateRange.to, p => Number(p.amount) || 0);
  const revenuePrev = sumInRange(paidPayments, 'created_at', dateRange.prevFrom, dateRange.prevTo, p => Number(p.amount) || 0);

  // Impacto ROI
  const roiTotals = data.roi.reduce((acc, r) => {
    const benefits = r.benefits ?? {};
    const hoursSaved = Number(benefits?.hours_saved || benefits?.horas_ahorradas || 0);
    const moneySaved = Number(benefits?.money_saved || benefits?.dinero_ahorrado || benefits?.revenue_increase || 0);
    const costs = (Number(r.cost_subscriptions) || 0) + (Number(r.cost_tokens) || 0)
                + (Number(r.cost_personnel) || 0) + (Number(r.cost_training) || 0);
    return { hours: acc.hours + hoursSaved, money: acc.money + moneySaved, costs: acc.costs + costs };
  }, { hours: 0, money: 0, costs: 0 });

  const certsWithDate = data.certificates.filter(c => !!c.generated_at) as { id: string; generated_at: string }[];
  const certificatesInRange = countInRange(certsWithDate, 'generated_at', dateRange.from, dateRange.to);
  const certificatesPrev = countInRange(certsWithDate, 'generated_at', dateRange.prevFrom, dateRange.prevTo);

  // ---------- Series temporales ----------
  const usersSeries = bucketRows(data.profiles, 'created_at', dateRange, granularity);
  const projectsCreatedSeries = bucketRows(data.projects, 'created_at', dateRange, granularity);
  const projectsSubmitted = data.projects.filter(p => !!p.submitted_at) as { submitted_at: string }[];
  const projectsSubmittedSeries = bucketRows(projectsSubmitted, 'submitted_at', dateRange, granularity);
  const combinedProjectsSeries = usersSeries.map((_, i) => ({
    label: projectsCreatedSeries[i]?.label ?? '',
    creados: projectsCreatedSeries[i]?.value ?? 0,
    submitted: projectsSubmittedSeries[i]?.value ?? 0,
  }));
  const revenueSeries = bucketRows(paidPayments, 'created_at', dateRange, granularity, p => Number(p.amount) || 0);
  const subsSeries = bucketRows(data.subscriptions, 'created_at', dateRange, granularity);
  const certSeries = bucketRows(certsWithDate, 'generated_at', dateRange, granularity);

  // Enrollment funnel
  const enrollmentsInRange = data.enrollments.filter(e => new Date(e.created_at) >= dateRange.from && new Date(e.created_at) <= dateRange.to);
  const enrollmentCompleted = enrollmentsInRange.filter(e => e.status === 'completed').length;
  const enrollmentConversion = enrollmentsInRange.length > 0
    ? Math.round((enrollmentCompleted / enrollmentsInRange.length) * 100) : 0;

  // Mix solución
  const solutionMix = [1,2,3,4,5].map(t => ({
    name: SOLUTION_LABELS[t],
    value: data.projects.filter(p => p.solution_type === t).length,
  })).filter(x => x.value > 0);

  // Top organizaciones
  const orgProjectCount = new Map<string, number>();
  const orgUserCount = new Map<string, number>();
  for (const p of data.profiles) {
    if (p.organization_id) orgUserCount.set(p.organization_id, (orgUserCount.get(p.organization_id) ?? 0) + 1);
  }
  const userOrgMap = new Map(data.profiles.map(p => [p.id, p.organization_id]));
  for (const pr of data.projects) {
    const orgId = userOrgMap.get(pr.user_id);
    if (orgId) orgProjectCount.set(orgId, (orgProjectCount.get(orgId) ?? 0) + 1);
  }
  const topOrgs = data.organizations
    .map(o => ({
      ...o,
      users: orgUserCount.get(o.id) ?? 0,
      projects: orgProjectCount.get(o.id) ?? 0,
    }))
    .sort((a, b) => b.users - a.users)
    .slice(0, 8);

  // Embudo
  const funnelData = [
    { name: 'Creados', value: data.projects.length },
    { name: 'En desarrollo', value: data.projects.filter(p => p.status === 'in_progress').length },
    { name: 'Submitted', value: submittedProjects },
    { name: 'Evaluados', value: evaluatedProjects },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            Panel Ejecutivo · LIDER IA
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Visión estratégica de crecimiento, actividad, proyectos e ingresos.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={range} onValueChange={(v) => { setRange(v as RangeKey); setGranularity(defaultGranularity(v as RangeKey)); }}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Últimos 7 días</SelectItem>
              <SelectItem value="30d">Últimos 30 días</SelectItem>
              <SelectItem value="90d">Últimos 90 días</SelectItem>
              <SelectItem value="12m">Últimos 12 meses</SelectItem>
              <SelectItem value="ytd">Año en curso</SelectItem>
              <SelectItem value="all">Todo el histórico</SelectItem>
            </SelectContent>
          </Select>
          <Tabs value={granularity} onValueChange={(v) => setGranularity(v as Granularity)}>
            <TabsList>
              <TabsTrigger value="day">Día</TabsTrigger>
              <TabsTrigger value="week">Semana</TabsTrigger>
              <TabsTrigger value="month">Mes</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="resumen">Resumen</TabsTrigger>
          <TabsTrigger value="asistentes">Asistentes IA</TabsTrigger>
          <TabsTrigger value="retos">Retos</TabsTrigger>
          <TabsTrigger value="contenidos">Contenidos</TabsTrigger>
          <TabsTrigger value="autoaprendizaje">Autoaprendizaje</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="plataforma">Uso de plataforma</TabsTrigger>
        </TabsList>

        <TabsContent value="resumen" className="space-y-6">
      {/* KPIs */}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Usuarios totales"
          value={totalUsers.toLocaleString('es-CO')}
          hint={`+${newUsers} nuevos`}
          delta={pctDelta(newUsers, newUsersPrev)}
          icon={<Users className="w-4 h-4" />}
          accent="primary"
        />
        <KpiCard
          label="Organizaciones activas"
          value={activeOrgs}
          hint={`${activeCohorts} cohortes activas`}
          icon={<Building2 className="w-4 h-4" />}
          accent="info"
        />
        <KpiCard
          label="Actividad diaria (DAU)"
          value={dau}
          hint={`WAU ${wau} · MAU ${mau}`}
          icon={<Activity className="w-4 h-4" />}
          accent="accent"
        />
        <KpiCard
          label="Proyectos creados"
          value={newProjects}
          hint={`${data.projects.length} totales`}
          delta={pctDelta(newProjects, newProjectsPrev)}
          icon={<Rocket className="w-4 h-4" />}
          accent="success"
        />
        <KpiCard
          label="Proyectos submitted"
          value={submittedProjects}
          hint={`${data.projects.length > 0 ? Math.round((submittedProjects / data.projects.length) * 100) : 0}% del total`}
          icon={<Trophy className="w-4 h-4" />}
          accent="warning"
        />
        <KpiCard
          label="Evaluaciones IA"
          value={evaluatedProjects}
          hint={`${submittedProjects > 0 ? Math.round((evaluatedProjects / submittedProjects) * 100) : 0}% de submitted`}
          icon={<Sparkles className="w-4 h-4" />}
          accent="primary"
        />
        <KpiCard
          label="Ingresos periodo"
          value={`$${Math.round(revenueInRange).toLocaleString('es-CO')}`}
          hint={`${activeSubs} suscripciones activas`}
          delta={pctDelta(revenueInRange, revenuePrev)}
          icon={<DollarSign className="w-4 h-4" />}
          accent="success"
        />
        <KpiCard
          label="Certificados emitidos"
          value={certificatesInRange}
          hint={`${data.certificates.length} totales`}
          delta={pctDelta(certificatesInRange, certificatesPrev)}
          icon={<GraduationCap className="w-4 h-4" />}
          accent="accent"
        />
      </div>

      {/* Fila 1: crecimiento + actividad */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Crecimiento de usuarios</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={usersSeries}>
                <defs>
                  <linearGradient id="grUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Area type="monotone" dataKey="value" name="Nuevos" stroke="hsl(var(--primary))" fill="url(#grUsers)" strokeWidth={2} />
                <Line type="monotone" dataKey="cumulative" name="Acumulado" stroke="hsl(var(--secondary))" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Proyectos: creados vs submitted</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={combinedProjectsSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Legend />
                <Bar dataKey="creados" name="Creados" fill="hsl(var(--primary))" radius={[4,4,0,0]} />
                <Bar dataKey="submitted" name="Submitted" fill="hsl(var(--success))" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Fila 2: ingresos + suscripciones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Ingresos y suscripciones</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={revenueSeries.map((r, i) => ({ label: r.label, ingresos: r.value, suscripciones: subsSeries[i]?.value ?? 0 }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis yAxisId="left" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis yAxisId="right" orientation="right" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="ingresos" name="Ingresos ($)" stroke="hsl(var(--success))" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="suscripciones" name="Nuevas suscripciones" stroke="hsl(var(--info))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Certificados emitidos</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={certSeries}>
                <defs>
                  <linearGradient id="grCert" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Area type="monotone" dataKey="value" name="Certificados" stroke="hsl(var(--accent))" fill="url(#grCert)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Fila 3: mix + embudo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Mix por tipo de solución</CardTitle></CardHeader>
          <CardContent>
            {solutionMix.length === 0 ? (
              <p className="text-muted-foreground text-center py-16">Sin datos aún.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={solutionMix} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={4}>
                    {solutionMix.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-base">Embudo de proyectos</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" width={110} fontSize={12} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0,6,6,0]} />
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-muted-foreground mt-2">
              Conversión enrollments (periodo): <span className="font-semibold text-foreground">{enrollmentConversion}%</span>
              {' · '}
              {enrollmentCompleted}/{enrollmentsInRange.length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Fila 4: top orgs + impacto */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Top organizaciones</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {topOrgs.length === 0 && <p className="text-muted-foreground text-center py-8">Sin organizaciones.</p>}
              {topOrgs.map(o => (
                <div key={o.id} className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center overflow-hidden shrink-0">
                      {o.logo_url ? <img src={o.logo_url} alt="" className="w-full h-full object-cover" /> : '🏢'}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">{o.name}</p>
                      <p className="text-xs text-muted-foreground">{o.users} usuarios · {o.projects} proyectos</p>
                    </div>
                  </div>
                  <TrendingUp className="w-4 h-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Impacto agregado (ROI reportado)</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-success/10 rounded-xl">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Horas ahorradas</p>
                <p className="text-3xl font-bold text-success mt-2">{Math.round(roiTotals.hours).toLocaleString('es-CO')}</p>
              </div>
              <div className="p-4 bg-primary/10 rounded-xl">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Beneficio $</p>
                <p className="text-3xl font-bold text-primary mt-2">${Math.round(roiTotals.money).toLocaleString('es-CO')}</p>
              </div>
              <div className="p-4 bg-warning/10 rounded-xl">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Inversión $</p>
                <p className="text-3xl font-bold text-warning mt-2">${Math.round(roiTotals.costs).toLocaleString('es-CO')}</p>
              </div>
              <div className="p-4 bg-accent/10 rounded-xl">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">ROI</p>
                <p className="text-3xl font-bold text-accent mt-2">
                  {roiTotals.costs > 0 ? `${Math.round(((roiTotals.money - roiTotals.costs) / roiTotals.costs) * 100)}%` : '—'}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Sumado sobre {data.roi.length} proyectos con reporte de ROI.
            </p>
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        <TabsContent value="asistentes">
          <AssistantsTab data={usage} isLoading={usageLoading} granularity={granularity} />
        </TabsContent>
        <TabsContent value="retos">
          <ChallengesTab data={usage} isLoading={usageLoading} granularity={granularity} />
        </TabsContent>
        <TabsContent value="contenidos">
          <ContentTab data={usage} isLoading={usageLoading} granularity={granularity} />
        </TabsContent>
        <TabsContent value="autoaprendizaje">
          <SelfPacedTab data={selfPaced} isLoading={selfPacedLoading} granularity={granularity} />
        </TabsContent>
        <TabsContent value="engagement">
          <EngagementTab data={usage} isLoading={usageLoading} />
        </TabsContent>
        <TabsContent value="plataforma">
          <PlatformUsageTab data={usage} isLoading={usageLoading} granularity={granularity} />
        </TabsContent>
      </Tabs>
    </div>

  );
}
